import { auth } from './firebaseConfig';
import { ref, set, onDisconnect, onValue, getDatabase, remove } from 'firebase/database';
import { toast } from 'react-toastify';

class PresenceTracker {
  constructor() {
    this.unsubscribe = null;
    this.heartbeatInterval = null;
    this.HEARTBEAT_INTERVAL = 15000;
    this.INACTIVE_THRESHOLD = 45000;
    this.isTracking = false;
    this.authUnsubscribe = null;
    this.onlineUsersSubscriptions = new Set();
    this.initializationPromise = null;
    
    // Bind methods that will be used in callbacks
    this.stopHeartbeat = this.stopHeartbeat.bind(this);
    this.cleanup = this.cleanup.bind(this);
    this.handleConnectionError = this.handleConnectionError.bind(this);
  }

  async startTracking() {
    if (this.isTracking) return;

    const user = auth.currentUser;
    if (!user) {
      console.error('Presence tracking requires authenticated user');
      return;
    }

    // Create a promise that resolves when initialization is complete
    this.initializationPromise = new Promise(async (resolve, reject) => {
      try {
        const db = getDatabase();
        const userStatusRef = ref(db, `/status/${user.uid}`);
        
        // Clean up any existing status
        await remove(userStatusRef);

        // Set up connection monitoring
        const connectedRef = ref(db, '.info/connected');
        this.cleanup(); // Clear previous connections

        this.unsubscribe = onValue(connectedRef, async (snapshot) => {
          // If we're not authenticated anymore, clean up
          if (!auth.currentUser) {
            this.cleanup();
            return;
          }

          // If we're connected
          if (snapshot.val() === true) {
            try {
              // When we disconnect, remove the status
              await onDisconnect(userStatusRef).remove();

              // Set our initial online status
              const presencePayload = {
                online: true,
                lastSeen: Date.now(),
                uid: user.uid
              };
              await set(userStatusRef, presencePayload);

              this.startHeartbeat(userStatusRef, user.uid);
              this.isTracking = true;
              resolve(); // Resolve the promise once we're fully initialized
            } catch (error) {
              if (error.code === 'PERMISSION_DENIED') {
                console.error('Permission denied for presence tracking');
                this.cleanup();
                reject(error);
              } else {
                console.error('Error setting online status:', error);
                reject(error);
              }
            }
          }
        }, (error) => {
          if (error.code === 'PERMISSION_DENIED') {
            console.error('Permission denied for connection tracking');
            this.cleanup();
            reject(error);
          }
        });

        // Monitor auth state changes
        this.authUnsubscribe = auth.onAuthStateChanged((user) => {
          if (!user) this.cleanup();
        });

      } catch (error) {
        console.error('Presence tracking initialization failed:', error);
        this.cleanup();
        reject(error);
      }
    });

    return this.initializationPromise;
  }

  startHeartbeat(userStatusRef, uid) {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(async () => {
      if (!this.isTracking || !auth.currentUser || auth.currentUser.uid !== uid) {
        this.cleanup();
        return;
      }

      try {
        await set(userStatusRef, {
          online: true,
          lastSeen: Date.now(),
          uid: uid
        });
      } catch (error) {
        if (error.code === 'PERMISSION_DENIED') {
          console.error('Permission denied for heartbeat');
          this.cleanup();
        }
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  async subscribeToOnlineUsers(callback) {
    // Check if user is authenticated
    if (!auth.currentUser) {
      console.error('Must be authenticated to subscribe to online users');
      callback([]);
      return () => {};
    }

    // Wait for presence tracking to be initialized
    try {
      if (!this.isTracking) {
        await this.startTracking();
      }

      const db = getDatabase();
      const statusRef = ref(db, '/status');
      
      const unsubscribe = onValue(statusRef, (snapshot) => {
        // Check authentication again in callback
        if (!auth.currentUser) {
          unsubscribe();
          this.onlineUsersSubscriptions.delete(unsubscribe);
          callback([]);
          return;
        }

        const onlineUsers = [];
        const now = Date.now();

        snapshot.forEach((childSnapshot) => {
          const status = childSnapshot.val();
          if (status?.online && status?.lastSeen && (now - status.lastSeen) < this.INACTIVE_THRESHOLD) {
            onlineUsers.push(childSnapshot.key);
          }
        });

        callback(onlineUsers);
      }, (error) => {
        if (error.code === 'PERMISSION_DENIED') {
          console.error('Permission denied for online users subscription');
          this.onlineUsersSubscriptions.delete(unsubscribe);
        } else {
          console.error('Online users subscription error:', error);
        }
        callback([]);
      });

      this.onlineUsersSubscriptions.add(unsubscribe);
      return () => {
        try {
          unsubscribe();
          this.onlineUsersSubscriptions.delete(unsubscribe);
        } catch (error) {
          console.error('Error unsubscribing from online users:', error);
        }
      };
    } catch (error) {
      console.error('Error setting up online users subscription:', error);
      callback([]);
      return () => {};
    }
  }

  async cleanup() {
    this.isTracking = false;
    this.stopHeartbeat();

    // Clean up all online users subscriptions
    this.onlineUsersSubscriptions.forEach(unsubscribe => {
      try {
        unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing from online users:', error);
      }
    });
    this.onlineUsersSubscriptions.clear();

    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        const db = getDatabase();
        const userStatusRef = ref(db, `/status/${userId}`);
        await remove(userStatusRef);
      }
    } catch (error) {
      console.error('Cleanup error:', error);
    }

    if (this.unsubscribe) this.unsubscribe();
    if (this.authUnsubscribe) this.authUnsubscribe();
    
    this.unsubscribe = null;
    this.authUnsubscribe = null;
  }

  getDeviceInfo() {
    return {
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      screen: `${window.screen.width}x${window.screen.height}`,
    };
  }

  handleConnectionError(error) {
    console.error('Presence tracking error:', error);
    if (error.code === 'PERMISSION_DENIED') {
      toast.error('Presence tracking permission denied');
      this.cleanup();
    }
  }
}

export const presenceTracker = new PresenceTracker();